import { useForm } from 'react-hook-form';
import SuccessAlert from '../Alert/SuccessAlert';
import Disclaimer from '../FormSection/Disclaimer';
import InputError from '../Input/InputError';

type FormData = {
  inquiry: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
};

type InputType = 'input' | 'select';

function baseClassName(type: InputType) {
  if (type === 'select') {
    return 'mt-1 block w-full pl-3 pr-10 py-2 text-base focus:outline-none sm:text-sm rounded-md';
  }
  return 'block w-full px-4 py-3 rounded-md shadow-sm';
}

function inputClassName(hasError: boolean, type: InputType = 'input') {
  const base = baseClassName(type);
  return (
    base +
    ' ' +
    (hasError
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary focus:border-primary')
  );
}

export default function ContactForm() {
  const methods = useForm<FormData>({
    defaultValues: {
      inquiry: 'support',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      message: '',
    },
  });
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      // isValid,
      isValidating,
      isSubmitting,
      isSubmitSuccessful,
    },
  } = methods;
  const disabled = isValidating || isSubmitting || isSubmitSuccessful;
  // const disabled = !isValid || isValidating || isSubmitting;
  const onSubmit = async (values: FormData) => {
    // console.log(values);
    await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(values),
    });
  };

  return (
    <>
      {isSubmitSuccessful ? (
        <div className="sm:col-span-2">
          <SuccessAlert title="Thanks! We'll be reaching out shortly." />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          method="POST"
          className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
        >
          <div className="sm:col-span-2">
            <label
              htmlFor="inquiry"
              className="block text-sm font-medium text-gray-700"
            >
              Inquiry Type
            </label>
            <div className="mt-1">
              <select
                id="inquiry"
                {...register('inquiry', {
                  required: 'Inquiry Type is required',
                })}
                className={inputClassName(!!errors.inquiry)}
              >
                <option value="support">Customer Service</option>
                <option value="support">Sales & Product Advice</option>
                <option value="sourcing">Partnerships & Media</option>
              </select>
              <InputError error={errors.inquiry} style="block" />
            </div>
          </div>
          <div>
            <label
              htmlFor="first-name"
              className="block text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="first-name"
                autoComplete="given-name"
                {...register('firstName', {
                  required: 'First Name is required',
                })}
                className={inputClassName(!!errors.firstName)}
              />
              <InputError error={errors.firstName} style="block" />
            </div>
          </div>
          <div>
            <label
              htmlFor="last-name"
              className="block text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="last-name"
                autoComplete="family-name"
                {...register('lastName', { required: 'Last Name is required' })}
                className={inputClassName(!!errors.lastName)}
              />
              <InputError error={errors.lastName} style="block" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <div className="mt-1">
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={inputClassName(!!errors.email)}
                {...register('email', { required: 'Email is required' })}
              />
              <InputError error={errors.email} style="block" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700"
            >
              Additional Comments (optional)
            </label>
            <div className="mt-1">
              <textarea
                rows={4}
                id="message"
                className={inputClassName(!!errors.message)}
                {...register('message')}
              />
              <InputError error={errors.message} style="block" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Disclaimer />
            <button
              type="submit"
              disabled={disabled}
              className="inline-flex items-center justify-center w-full px-6 py-3 text-base font-medium text-white border border-transparent rounded-md shadow-sm bg-primary hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Submit
            </button>
          </div>
        </form>
      )}
    </>
  );
}
